// Brain Blitz: 60-second arcade. Smash the block with the right answer — combos, time bonuses, high scores.
import * as THREE from 'three';
import { VoxelWorld, blockGeometry } from './voxel.js';
import { B } from './blocks.js';
import { THEMES } from './themes.js';
import { generateBlitzArena } from './levelgen.js';
import { Player } from './player.js';
import { Pet } from './pet.js';
import { makeLabelTexture } from './textures.js';
import { audio } from './audio.js';
import { haptics } from './haptics.js';
import { easeOutBack } from './util.js';

const COLORS = ['#ff4d6d', '#3d8bff', '#ffc21f', '#2fc85a'];
const ORES = [B.RUBY_ORE, B.DIAMOND_ORE, B.GOLD_ORE, B.EMERALD_BLOCK];
export const BLITZ_MODES = {
  tables: { name: 'Times Tables', icon: '✖️', weights: { mul: 1 } },
  division: { name: 'Division', icon: '➗', weights: { div: 1, missing: 0.3 } },
  addsub: { name: 'Add & Take Away', icon: '➕', weights: { add: 1, sub: 1 } },
  mixed: { name: 'Mega Mix', icon: '🌀', weights: { mul: 2, div: 1, add: 1, sub: 1, missing: 1, pattern: 0.5 } },
};

class AnswerBlock {
  constructor(mode, i) {
    this.mode = mode;
    this.i = i;
    const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
    this.base = new THREE.Vector3(0.5 + Math.sin(a) * 5.5, 41, 0.5 + Math.cos(a) * 5.5);
    this.group = new THREE.Group();
    this.group.position.copy(this.base);
    this.mesh = new THREE.Mesh(blockGeometry(ORES[i]), mode.game.mats.solid);
    this.mesh.scale.setScalar(1.5);
    this.mesh.position.y = 0.75;
    this.mesh.castShadow = true;
    this.glowMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(COLORS[i]).multiplyScalar(1.7), transparent: true, opacity: 0.35, depthWrite: false });
    this.glow = new THREE.Mesh(new THREE.BoxGeometry(1.62, 1.62, 1.62), this.glowMat);
    this.glow.position.y = 0.75;
    this.label = new THREE.Sprite(new THREE.SpriteMaterial({ transparent: true, depthWrite: false }));
    this.label.position.y = 2.6;
    this.label.scale.set(2, 2, 1);
    this.group.add(this.mesh, this.glow, this.label);
    mode.group.add(this.group);
    this.rise = 1;
    this.broken = false;
  }
  set(text) {
    if (this.label.material.map) this.label.material.map.dispose();
    this.label.material.map = makeLabelTexture(text, { bg: '#1d2236', border: COLORS[this.i] });
    this.label.material.needsUpdate = true;
    this.broken = false;
    this.group.visible = true;
    this.rise = 0;
    this.mesh.material = this.mode.game.mats.solid;
  }
  update(dt, t) {
    if (this.rise < 1) {
      this.rise = Math.min(1, this.rise + dt * 3.2);
      const e = easeOutBack(this.rise);
      this.mesh.position.y = -1.5 + e * 2.25;
      this.glow.position.y = this.mesh.position.y;
      this.label.scale.setScalar(2.1 * Math.min(1, this.rise * 1.3));
    }
    this.glowMat.opacity = this.broken ? 0 : 0.25 + Math.sin(t * 5 + this.i) * 0.12 + (this.flashGreen > 0 ? 0.5 : 0);
    if (this.flashGreen > 0) {
      this.flashGreen -= dt;
      this.glowMat.color.set('#50ff80').multiplyScalar(2);
    } else this.glowMat.color.set(COLORS[this.i]).multiplyScalar(1.7);
    this.mesh.rotation.y = Math.sin(t * 1.5 + this.i) * 0.08;
  }
  smash(good) {
    this.broken = true;
    this.mode.fx.particles.blockBreak(this.mesh.getWorldPosition(new THREE.Vector3()), good ? ORES[this.i] : B.COBBLE);
    this.group.visible = false;
  }
}

export class BlitzMode {
  constructor(game, modeKey) {
    this.game = game;
    this.modeKey = modeKey;
    this.mode = BLITZ_MODES[modeKey] || BLITZ_MODES.tables;
    this.group = new THREE.Group();
    game.scene.add(this.group);
    this.fx = game.fx;
    this.colliders = [];
    this.hittables = [];
    this.stats = { jumps: 0 };
    this.time = 0;
    this.state = 'countdown';
    this.timeLeft = 60;
    this.score = 0;
    this.combo = 0;
    this.bestCombo = 0;
    this.correct = 0;
    this.wrong = 0;
    this.forceDoubleJump = true;
  }

  build(themeKey) {
    const g = this.game;
    this.themeKey = themeKey;
    this.theme = THEMES[themeKey];
    g.applyTheme(themeKey);
    this.world = new VoxelWorld(g.mats);
    this.group.add(this.world.group);
    const spec = generateBlitzArena(this.world, this.theme);
    this.killY = spec.killY;
    this.world.rebuild();
    this.character = g.makePlayerCharacter();
    this.group.add(this.character.root);
    this.player = new Player(this, this.character);
    this.player.spawn(new THREE.Vector3(0.5, 41, -1.5), 0);
    this.spawnPos = new THREE.Vector3(0.5, 41, -1.5);
    const pet = g.save.data.equip.pet;
    if (pet && pet !== 'none') this.pet = new Pet(this, pet);
    this.blocks = [0, 1, 2, 3].map((i) => new AnswerBlock(this, i));
    for (const b of this.blocks) {
      b.group.visible = false;
      this.colliders.push({ min: new THREE.Vector3(b.base.x - 0.75, b.base.y, b.base.z - 0.75), max: new THREE.Vector3(b.base.x + 0.75, b.base.y + 1.5, b.base.z + 0.75), solid: true });
    }
    this.previousCamera={dist:g.camRig.dist,pitch:g.camRig.pitch};
    this.cameraFocus=new THREE.Vector3(.5,41,.5);
    g.camRig.yaw = 0;
    g.camRig.pitch = 0.88;
    g.camRig.dist = 16;
    g.camRig.snap(this.cameraFocus, 0);
    this.countT = 0;
    this.lastCount = 4;
    this.game.ui.blitzHud.show(this);
  }

  newQuestion() {
    const g = this.game;
    const boost = Math.floor(this.correct / 6) * 0.35;
    const focus = g.save.data.settings.tables;
    this.q = g.qe.next({ weights: this.mode.weights, tables: focus && focus.length ? focus : [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], numeric: true, boost });
    this.answerIdx = this.q.choices.indexOf(this.q.answer);
    this.blocks.forEach((b, i) => b.set(this.q.choices[i]));
    this.qStart = this.time;
    this.firstTry = true;
    this.game.ui.blitzHud.question(this.q.text);
    if (g.save.data.settings.readAloud) audio.speak(this.q.speakText || this.q.text.replace(/<[^>]+>/g, ''));
  }

  update(dt, input) {
    const g = this.game;
    this.time += dt;
    const t = this.time;
    for (const b of this.blocks) b.update(dt, t);
    if (this.state === 'countdown') {
      this.countT += dt;
      const n = 3 - Math.floor(this.countT / 0.8);
      if (n !== this.lastCount && n >= 1) {
        this.lastCount = n;
        g.ui.blitzHud.count(String(n));
        audio.play('countdown');
      }
      if (this.countT >= 2.4) {
        this.state = 'play';
        g.ui.blitzHud.count('GO!');
        audio.play('go');
        this.newQuestion();
      }
    } else if (this.state === 'play') {
      this.timeLeft -= dt;
      if (this.timeLeft <= 10 && Math.ceil(this.timeLeft) !== this.lastTick) {
        this.lastTick = Math.ceil(this.timeLeft);
        audio.play('tick');
      }
      if (this.timeLeft <= 0) return this.finish();
      if (this.dashT !== undefined) this.updateDash(dt);
      else {
        if (input.actionPressed) this.player.swing();
        for (let i = 0; i < 4; i++) if (input.wasPressed(String(i + 1))) this.startDash(i);
        for (const tap of input.taps) {
          const ray = new THREE.Raycaster();
          ray.setFromCamera(new THREE.Vector2((tap.x / innerWidth) * 2 - 1, -(tap.y / innerHeight) * 2 + 1), g.camera);
          for (const b of this.blocks) if (!b.broken && ray.intersectObjects([b.mesh, b.label], false).length) this.startDash(b.i);
        }
      }
    }
    this.player.update(dt, this.state === 'play' && this.dashT === undefined ? input : NOI, g.camRig.yaw);
    if (this.pet) this.pet.update(dt);
    this.cameraFocus.set(.5+(this.player.pos.x-.5)*.15,41,.5+(this.player.pos.z-.5)*.15);
    g.camRig.update(dt, this.state === 'play' ? input : NOI, this.cameraFocus, this.world);
    g.ui.blitzHud.update(this);
  }

  startDash(i) {
    const b = this.blocks[i];
    if (b.broken || this.dashT !== undefined) return;
    const p = this.player.pos;
    const dir = new THREE.Vector3(b.base.x - p.x, 0, b.base.z - p.z);
    const d = dir.length();
    dir.normalize();
    this.dashFrom = p.clone();
    this.dashTo = new THREE.Vector3(b.base.x - dir.x * 1.5, b.base.y, b.base.z - dir.z * 1.5);
    this.dashT = 0;
    this.dashIdx = i;
    this.player.facing = Math.atan2(dir.x, dir.z);
    if (d > 2.2) audio.play('dash');
    if (d > 2.2) haptics.play('dash');
  }

  updateDash(dt) {
    this.dashT += dt / 0.2;
    const k = Math.min(1, this.dashT);
    this.player.pos.lerpVectors(this.dashFrom, this.dashTo, 1 - Math.pow(1 - k, 3));
    this.player.vel.set(0, 0, 0);
    this.fx.particles.trail(this.player.pos, 'sparkle', this.time);
    if (k >= 1) {
      const i = this.dashIdx;
      this.dashT = undefined;
      this.player.swingT = 0;
      this.player.swingHitDone = true;
      audio.play('swing');
      this.choose(i);
    }
  }

  // Player API used by Player.swing -> level.swordHit
  swordHit(player) {
    const fx = Math.sin(player.facing), fz = Math.cos(player.facing);
    let best = null, bd = 9;
    for (const b of this.blocks) {
      if (b.broken) continue;
      const dx = b.base.x - player.pos.x, dz = b.base.z - player.pos.z;
      const d = Math.hypot(dx, dz);
      if (d < 2.6 && (d < 1.2 || (dx * fx + dz * fz) / d > 0.1) && d < bd) {
        best = b;
        bd = d;
      }
    }
    if (best && this.state === 'play') this.choose(best.i);
  }

  choose(i) {
    if (this.state !== 'play') return;
    const b = this.blocks[i];
    if (b.broken) return;
    const g = this.game;
    const correct = i === this.answerIdx;
    if (this.firstTry) {
      g.qe.record(this.q, correct, true);
      g.onAnswer(correct, true);
    }
    const pos = b.mesh.getWorldPosition(new THREE.Vector3()).add(new THREE.Vector3(0, 1, 0));
    if (correct) {
      const speed = this.time - this.qStart;
      if (this.firstTry) {
        this.combo++;
        this.correct++;
      }
      this.bestCombo = Math.max(this.bestCombo, this.combo);
      const pts = this.firstTry ? 100 + Math.min(10, this.combo) * 25 + Math.max(0, Math.round(100 * (1 - (speed - 1.5) / 6))) : 25;
      this.score += pts;
      this.timeLeft = Math.min(99, this.timeLeft + (this.firstTry ? 2 : 0.5));
      b.smash(true);
      audio.play('correct');
      haptics.play(this.combo >= 3 ? 'combo' : 'correct');
      if (this.combo >= 3) audio.play('combo', { streak: this.combo });
      this.fx.particles.burst(pos, { count: 26, colors: [COLORS[i], '#ffffff', '#ffd23f'], speed: 6, up: 6, glow: true, bright: 2, size: 0.13 });
      this.fx.worldUI.float(`+${pts}`, pos, 'ft-gold ft-big');
      if (this.combo >= 3 && this.firstTry) this.fx.worldUI.float(`🔥 x${this.combo} COMBO!`, pos.clone().add(new THREE.Vector3(0, 1, 0)), 'ft-red', { life: 1.2 });
      g.camRig.shake(0.3);
      this.state = 'between';
      setTimeout(() => {
        if (this.disposed || this.state !== 'between') return;
        this.state = 'play';
        this.newQuestion();
      }, 380);
    } else {
      this.firstTry = false;
      this.combo = 0;
      this.wrong++;
      this.timeLeft = Math.max(0.1, this.timeLeft - 3);
      b.smash(false);
      audio.play('wrong');
      haptics.play('wrong');
      this.blocks[this.answerIdx].flashGreen = 2;
      this.fx.worldUI.float('−3s', pos, 'ft-red ft-big');
      g.ui.blitzHud.hint(this.q.explain.replace(/<span class="tip">[\s\S]*?<\/span>/g, '').replace(/<br>/g, ' '));
    }
  }

  // Player falls off: put back in the middle
  playerDied() {
    this.player.spawn(this.spawnPos.clone(), this.player.facing);
    this.timeLeft = Math.max(0.1, this.timeLeft - 2);
    audio.play('whoosh');
  }
  onPlayerHurt() {}

  finish() {
    if (this.state === 'over') return;
    this.state = 'over';
    this.player.frozen = true;
    this.player.celebrate = true;
    audio.play('levelComplete');
    for (const b of this.blocks) b.group.visible = false;
    this.game.ui.blitzHud.question(null);
    this.game.ui.blitzHud.count("TIME'S UP!");
    setTimeout(() => {
      if (!this.disposed) this.game.blitzComplete({ score: this.score, correct: this.correct, wrong: this.wrong, bestCombo: this.bestCombo, mode: this.modeKey });
    }, 1800);
  }

  dispose() {
    if(this.previousCamera){this.game.camRig.dist=this.previousCamera.dist;this.game.camRig.pitch=this.previousCamera.pitch;}
    this.disposed = true;
    this.world.dispose();
    for (const b of this.blocks) if (b.label.material.map) b.label.material.map.dispose();
    if (this.pet) this.pet.dispose();
    this.character.dispose();
    this.game.scene.remove(this.group);
    this.fx.particles.clear();
    this.fx.worldUI.clear();
    this.fx.rings.clear();
    this.game.ui.blitzHud.hide();
  }
}

const NOI = { moveX: 0, moveY: 0, jumpPressed: false, jumpHeld: false, actionPressed: false, camDX: 0, camDY: 0, wheel: 0, taps: [], touchMode: false, wasPressed: () => false };
